import React, { useMemo, useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO, startOfDay, eachDayOfInterval, subDays, startOfWeek, isWithinInterval, subMonths, subQuarters, subYears, eachWeekOfInterval, eachMonthOfInterval, getMonth, getYear } from 'date-fns';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/ui/LoadingState';
import { ArrowRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  ChartContainer, 
  ChartLegend, 
  ChartLegendContent, 
  ChartTooltip, 
  ChartTooltipContent,
  type ChartConfig 
} from "@/components/ui/chart";

interface Lead {
  id: string;
  created_at: string;
  // ... other lead properties
}

interface LeadsPerDayProps {
  timeRange?: string; // Updated to use timeRange instead of days
}

const chartConfig = {
  count: {
    label: "Contacts",
    color: "hsl(var(--chart-1))"
  }
} satisfies ChartConfig;

const LeadsPerDay: React.FC<LeadsPerDayProps> = ({ timeRange = "7d" }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { get } = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        setIsLoading(true);
        const response = await get('/leads/');
        setLeads(response.data || []);
      } catch (error) {
        console.error('Error fetching leads:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeads();
  }, []);

  const getDateRangeFromTimeRange = (timeRange: string) => {
    const endDate = startOfDay(new Date());
    let startDate;
    
    switch (timeRange) {
      case "24h":
        // For 24h, we still use days but just show the last day
        startDate = startOfDay(subDays(endDate, 0));
        break;
      case "7d":
        startDate = startOfDay(subDays(endDate, 6));
        break;
      case "30d":
        startDate = startOfDay(subDays(endDate, 29));
        break;
      case "90d":
        startDate = startOfDay(subDays(endDate, 89));
        break;
      case "365d":
        startDate = startOfDay(subDays(endDate, 364));
        break;
      default:
        startDate = startOfDay(subDays(endDate, 6)); // Default to 7 days
    }
    
    return { startDate, endDate };
  };

  const chartData = useMemo(() => {
    const { startDate, endDate } = getDateRangeFromTimeRange(timeRange);
    
    let dateRange;
    let formatKey: (date: Date) => string;
    
    // For different time ranges, we adjust the granularity
    if (timeRange === "24h") {
      // For 24h, show hourly data
      dateRange = Array.from({ length: 24 }, (_, i) => {
        const date = new Date();
        date.setHours(i, 0, 0, 0);
        return date;
      });
      formatKey = (date: Date) => format(date, 'HH:00');
    } else if (timeRange === "7d" || timeRange === "30d") {
      // For 7d and 30d, show daily data
      dateRange = eachDayOfInterval({ start: startDate, end: endDate });
      formatKey = (date: Date) => format(date, 'yyyy-MM-dd');
    } else if (timeRange === "90d") {
      // For 90d, show weekly data
      dateRange = eachWeekOfInterval({ start: startDate, end: endDate });
      formatKey = (date: Date) => `Week of ${format(date, 'MMM d')}`;
    } else if (timeRange === "365d") {
      // For 365d, show monthly data
      dateRange = eachMonthOfInterval({ start: startDate, end: endDate });
      formatKey = (date: Date) => format(date, 'MMM yyyy');
    } else {
      // Default to daily
      dateRange = eachDayOfInterval({ start: startDate, end: endDate });
      formatKey = (date: Date) => format(date, 'yyyy-MM-dd');
    }
    
    // Initialize counts for each interval
    const counts = dateRange.map(date => ({
      date: formatKey(date),
      rawDate: date, // Store the raw date for filtering
      count: 0
    }));

    // Count leads per interval
    leads.forEach(lead => {
      const leadDate = parseISO(lead.created_at);
      
      // Find the appropriate interval for this lead
      let dataPoint;
      
      if (timeRange === "24h") {
        // Match by hour
        dataPoint = counts.find(d => new Date(d.rawDate).getHours() === leadDate.getHours());
      } else if (timeRange === "7d" || timeRange === "30d") {
        // Match by day
        dataPoint = counts.find(d => format(d.rawDate, 'yyyy-MM-dd') === format(leadDate, 'yyyy-MM-dd'));
      } else if (timeRange === "90d") {
        // Match by week
        dataPoint = counts.find(d => {
          const weekStart = d.rawDate;
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);
          return leadDate >= weekStart && leadDate <= weekEnd;
        });
      } else if (timeRange === "365d") {
        // Match by month
        dataPoint = counts.find(d => 
          getMonth(d.rawDate) === getMonth(leadDate) && 
          getYear(d.rawDate) === getYear(leadDate)
        );
      }
      
      if (dataPoint) {
        dataPoint.count++;
      }
    });

    return counts;
  }, [leads, timeRange]);

  // Format date based on time range for X-axis display
  const formatXAxisDate = (dateStr: string) => {
    if (timeRange === "24h") {
      // Already formatted as HH:00
      return dateStr;
    } else if (timeRange === "7d") {
      return format(parseISO(dateStr), 'EEE');
    } else if (timeRange === "30d") {
      return format(parseISO(dateStr), 'MMM d');
    } else if (timeRange === "90d" || timeRange === "365d") {
      // For weekly and monthly, keep as is 
      return dateStr;
    }
    return dateStr;
  };

  // For tooltip formatting
  const formatTooltipLabel = (value: string) => {
    if (timeRange === "24h") {
      return value; // Already formatted as HH:00
    } else if (timeRange === "7d" || timeRange === "30d") {
      return format(parseISO(value), 'MMM d, yyyy');
    } else {
      return value; // For weekly and monthly, keep as is
    }
  };

  // Calculate new leads for this week button
  const newLeads = leads.filter(lead => {
    const leadDate = parseISO(lead.created_at);
    const thisWeek = startOfWeek(new Date());
    return isWithinInterval(leadDate, {
        start: thisWeek,
        end: new Date()
    });
  });

  // Get appropriate tick count based on time range
  const getTickCount = () => {
    switch (timeRange) {
      case "24h": return 6; // Every 4 hours
      case "7d": return 7; // Every day
      case "30d": return 10; // Every 3 days
      case "90d": return 12; // Weekly
      case "365d": return 12; // Monthly
      default: return 7;
    }
  };

  return (
    <div className="px-2 pt-4 sm:px-6 sm:pt-6">
      {isLoading ? (
        <LoadingState label="Loading contacts..." className="min-h-[250px]" />
      ) : leads.length === 0 ? (
        <div className="h-[250px] flex items-center justify-center text-muted-foreground">
          No data yet!
        </div>
      ) : (
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] w-full"
        >
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="fillLeads" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-count)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-count)"
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={formatXAxisDate}
              tickCount={getTickCount()}
            />
            <YAxis tickLine={false} axisLine={false} />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  className="w-[150px]"
                  labelFormatter={formatTooltipLabel}
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="count"
              type="monotone"
              fill="url(#fillLeads)"
              stroke="var(--color-count)"
              name={chartConfig.count.label}
            />
            <ChartLegend 
              content={<ChartLegendContent />}
            />
          </AreaChart>
        </ChartContainer>
      )}
      <Button 
        variant="outline" 
        className="mt-4 w-full"
        onClick={() => navigate('/contacts?date_filter=today')}
      >
        View New Contacts {`(${newLeads.length})`} <ArrowRightIcon className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

export default LeadsPerDay;
